import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Send, Plus, Search, Clock, CheckCircle2, Eye, MousePointerClick,
  Pause, AlertCircle, MoreVertical, Trash2, Play, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import {
  useCampaigns, useDeleteCampaign, usePauseCampaign, useResumeCampaign, useCancelCampaign,
} from '@/hooks/useNewsletter';
import {
  CAMPAIGN_STATUS_LABELS, type Campaign, type CampaignStatus,
} from '@/types/newsletter';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

const STATUS_TONES: Record<CampaignStatus, 'neutral' | 'info' | 'brass' | 'success' | 'warning' | 'danger'> = {
  draft:     'neutral',
  scheduled: 'info',
  sending:   'brass',
  sent:      'success',
  paused:    'warning',
  cancelled: 'neutral',
  failed:    'danger',
};

export function CampaignsPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'newsletter', 'write') : false;

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | CampaignStatus>('');

  const { data, isLoading, error } = useCampaigns();
  const del = useDeleteCampaign();
  const pause = usePauseCampaign();
  const resume = useResumeCampaign();
  const cancel = useCancelCampaign();

  const [deleting, setDeleting] = useState<Campaign | null>(null);
  const [cancelling, setCancelling] = useState<Campaign | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((c) => {
      if (status && c.status !== status) return false;
      if (search.trim() && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [data, search, status]);

  const stats = useMemo(() => {
    if (!data) return { total: 0, scheduled: 0, sent: 0, sending: 0 };
    return {
      total: data.length,
      scheduled: data.filter((c) => c.status === 'scheduled').length,
      sending: data.filter((c) => c.status === 'sending').length,
      sent: data.filter((c) => c.status === 'sent').length,
    };
  }, [data]);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState icon={AlertCircle}
          title="Could not load campaigns"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      toast.success(`Deleted ${deleting.name}`);
      setDeleting(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  async function confirmCancel() {
    if (!cancelling) return;
    try {
      await cancel.mutateAsync(cancelling.id);
      toast.success(`Cancelled ${cancelling.name}`);
      setCancelling(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Cancel failed.');
    }
  }

  async function doPause(c: Campaign) {
    try { await pause.mutateAsync(c.id); toast.success(`Paused ${c.name}`); }
    catch (err) { toast.error((err as { message?: string }).message ?? 'Pause failed.'); }
  }

  async function doResume(c: Campaign) {
    try { await resume.mutateAsync(c.id); toast.success(`Resumed ${c.name}`); }
    catch (err) { toast.error((err as { message?: string }).message ?? 'Resume failed.'); }
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Stat label="Total"      value={String(stats.total)} />
        <Stat label="Scheduled"  value={String(stats.scheduled)} />
        <Stat label="Sending"    value={String(stats.sending)} accent={stats.sending > 0} />
        <Stat label="Sent"       value={String(stats.sent)} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Input placeholder="Search campaigns…" leftIcon={<Search size={14} />}
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="w-44">
          <Select value={status}
            onChange={(e) => setStatus(e.target.value as CampaignStatus | '')}
            options={[
              { value: '', label: 'All statuses' },
              ...Object.entries(CAMPAIGN_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l })),
            ]}
          />
        </div>
        {canWrite && (
          <Link to="/newsletter/campaigns/new">
            <Button leftIcon={<Plus size={13} />}>New Campaign</Button>
          </Link>
        )}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <Card>
          <EmptyState icon={Send}
            title={search || status ? 'No campaigns match your filters' : 'No campaigns yet'}
            description={search || status ? 'Try clearing some filters.' : 'Create your first email campaign — an announcement, greeting, or promotion.'}
            action={canWrite && !search && !status && (
              <Link to="/newsletter/campaigns/new">
                <Button leftIcon={<Plus size={14} />}>New Campaign</Button>
              </Link>
            )}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              canWrite={canWrite}
              openMenu={openMenu === c.id}
              onOpenMenu={() => setOpenMenu(openMenu === c.id ? null : c.id)}
              onCloseMenu={() => setOpenMenu(null)}
              onDelete={() => setDeleting(c)}
              onPause={() => doPause(c)}
              onResume={() => doResume(c)}
              onCancel={() => setCancelling(c)}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        loading={del.isPending}
        destructive
        title={`Delete ${deleting?.name}?`}
        confirmLabel="Delete campaign"
        description="This permanently removes the campaign draft. Sent campaigns cannot be deleted — cancel instead."
      />
      <ConfirmDialog
        open={!!cancelling}
        onClose={() => setCancelling(null)}
        onConfirm={confirmCancel}
        loading={cancel.isPending}
        destructive
        title={`Cancel ${cancelling?.name}?`}
        confirmLabel="Cancel campaign"
        description="In-progress sends will stop. Already-delivered emails cannot be recalled."
      />
    </div>
  );
}

function CampaignCard({
  campaign, canWrite, openMenu, onOpenMenu, onCloseMenu,
  onDelete, onPause, onResume, onCancel,
}: {
  campaign: Campaign;
  canWrite: boolean;
  openMenu: boolean;
  onOpenMenu: () => void;
  onCloseMenu: () => void;
  onDelete: () => void;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
}) {
  const m = campaign.metrics;
  const openRate = m && m.sent > 0 ? ((m.uniqueOpens / m.sent) * 100).toFixed(1) : null;
  const clickRate = m && m.sent > 0 ? ((m.uniqueClicks / m.sent) * 100).toFixed(1) : null;

  return (
    <Card className="relative hover:border-brass-400/60 transition-colors">
      <Link to={campaign.status === 'draft' ? `/newsletter/campaigns/${campaign.id}/edit` : `/newsletter/campaigns/${campaign.id}`} className="block">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge tone={STATUS_TONES[campaign.status]}>{CAMPAIGN_STATUS_LABELS[campaign.status]}</Badge>
            </div>
            <h3 className="font-display text-lg text-text truncate">{campaign.name}</h3>
            <p className="text-xs text-text-muted truncate mt-1">{campaign.subject}</p>
          </div>
        </div>

        {campaign.status === 'sent' && m && (
          <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-border">
            <Metric icon={CheckCircle2} label="Delivered" value={m.delivered.toLocaleString()} />
            <Metric icon={Eye}  label="Open rate"
              value={openRate ? `${openRate}%` : '—'} />
            <Metric icon={MousePointerClick} label="Click rate"
              value={clickRate ? `${clickRate}%` : '—'} />
          </div>
        )}

        {campaign.status === 'sending' && m && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-baseline justify-between mb-2 text-xs">
              <span className="text-text-muted">Sending progress</span>
              <span className="tabular text-text">{m.sent.toLocaleString()} / {m.queued.toLocaleString()}</span>
            </div>
            <div className="h-1 bg-bg-subtle rounded-full overflow-hidden">
              <div className="h-full bg-brass-400 transition-all"
                style={{ width: `${m.queued > 0 ? (m.sent / m.queued) * 100 : 0}%` }} />
            </div>
          </div>
        )}

        {campaign.status === 'scheduled' && campaign.scheduledFor && (
          <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 text-xs text-text-muted">
            <Clock size={11} />
            Scheduled for {new Date(campaign.scheduledFor).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
          </div>
        )}

        {campaign.status === 'draft' && (
          <div className="mt-4 pt-4 border-t border-border text-xs text-text-dim">
            {campaign.estimatedRecipientCount
              ? `Targeting ${campaign.estimatedRecipientCount.toLocaleString()} subscribers`
              : 'Not ready to send'}
          </div>
        )}
      </Link>

      {canWrite && (
        <div className="absolute top-3 right-3">
          <button
            onClick={onOpenMenu}
            className="p-1.5 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm"
          >
            <MoreVertical size={15} />
          </button>
          {openMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={onCloseMenu} />
              <div className="absolute right-0 top-full mt-1 z-20 w-44 bg-bg-raised border border-border-strong rounded-sm shadow-raised py-1">
                {campaign.status === 'sending' && (
                  <button onClick={() => { onPause(); onCloseMenu(); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                    <Pause size={13} /> Pause
                  </button>
                )}
                {campaign.status === 'paused' && (
                  <button onClick={() => { onResume(); onCloseMenu(); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                    <Play size={13} /> Resume
                  </button>
                )}
                {['scheduled', 'sending', 'paused'].includes(campaign.status) && (
                  <button onClick={() => { onCancel(); onCloseMenu(); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-warning hover:bg-warning/10 text-left">
                    <X size={13} /> Cancel
                  </button>
                )}
                {campaign.status === 'draft' && (
                  <button onClick={() => { onDelete(); onCloseMenu(); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left">
                    <Trash2 size={13} /> Delete
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function Metric({ icon: Icon, label, value }: { icon: typeof Send; label: string; value: string }) {
  return (
    <div>
      <div className="flex items-center gap-1 mb-1">
        <Icon size={10} className="text-text-dim" />
        <span className="kicker text-2xs">{label}</span>
      </div>
      <div className="text-sm tabular text-text">{value}</div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className={`font-display text-2xl tabular ${accent ? 'text-brass-300' : 'text-text'}`}>{value}</div>
    </div>
  );
}
